import type { PaymentSteps, PaymentStepsAttributes } from 'api/models';
import dayjs from 'dayjs';
import { isDefined, pipe, prop, sumBy, take } from 'remeda';

import type { PaymentStep } from './hooks';
import { PaymentPlanStatus } from './PaymentPlan.constants';

export const computeStatus = (cancelled: boolean, completed: boolean) => {
  if (cancelled) return PaymentPlanStatus.CANCELLED;
  if (completed) return PaymentPlanStatus.COMPLETED;
  return PaymentPlanStatus.IN_PROGRESS;
};

export const computeNextStepDate = (status: PaymentPlanStatus, step?: PaymentSteps) => {
  if (status !== PaymentPlanStatus.IN_PROGRESS) return '-';
  if (!isDefined(step)) return '-';

  return dayjs(step.attributes.due_date).format('LL');
};

export const adjustNextDates = (
  allSteps: Array<PaymentStep>,
  stepIndex: number,
  newDelay: number,
): Array<PaymentStep> => {
  let dueDate = dayjs(allSteps[stepIndex].dueDate);

  return allSteps.map((step, idx) => {
    if (idx <= stepIndex) return step;
    dueDate = dueDate.add(newDelay, 'day');
    return { ...step, dueDate: new Date(dueDate.toISOString()) };
  });
};

// This function, which differs only in case, exists because the payment plan module is split between v1 and v2.
export const adjustNextDatesSnakeCased = (
  allSteps: Array<PaymentStepsAttributes>,
  stepIndex: number,
  newDelay: number,
): Array<PaymentStepsAttributes> => {
  let dueDate = dayjs(allSteps[stepIndex].due_date);

  return allSteps.map((step, idx) => {
    if (idx <= stepIndex) return step;

    dueDate = dueDate.add(newDelay, 'day');
    return { ...step, due_date: dueDate.toISOString() };
  });
};

export const adjustNextAmounts = (
  allSteps: Array<PaymentStep>,
  stepIndex: number,
  targetTotal: number,
) => {
  const fixed = (n: number) => Math.round(n * 100) / 100;

  const previousStepsTotal = fixed(pipe(allSteps, take(stepIndex), sumBy(prop('amount'))));
  const remainingAmount = fixed(targetTotal - previousStepsTotal);
  const remainingSteps = allSteps.length - stepIndex;
  const remainingAmountPerStep = fixed(remainingAmount / remainingSteps);

  const updatedSteps = allSteps.map((step, idx) => {
    if (idx < stepIndex) return step;
    if (idx === allSteps.length - 1) return { ...step, amount: remainingAmount };
    return { ...step, amount: remainingAmountPerStep };
  });

  const newTotal = fixed(pipe(updatedSteps, sumBy(prop('amount'))));
  const roundingError = fixed(targetTotal - newTotal);
  const lastStep = updatedSteps[updatedSteps.length - 1];
  lastStep.amount = fixed(lastStep.amount + roundingError);

  return updatedSteps;
};
